import React from "react";
import { Check } from "lucide-react";
import { twMerge } from "tailwind-merge";
import FilterWrapper from "./FilterWrapper";

type Props<T extends string> = {
  icon: React.ReactNode;
  title: string;
  value: T;
  setValue: (value: T) => void;
  /** The real choices; the "no restriction" value is passed separately. */
  options: ReadonlyArray<{
    label: string;
    value: T;
  }>;
  anyValue: T;
  anyLabel: string;
  /**
   * "pills": toggle chips, tapping the selected one again clears it.
   * "segmented": equal-width joined steps with an explicit "Any" first, for
   * short numeric steps ("1+", "2+").
   */
  variant?: "pills" | "segmented";
  info?: React.ReactNode;
};

/** Single choice shown as tappable options instead of a hidden dropdown. */
const FilterChoice = <T extends string>({
  icon,
  title,
  value,
  setValue,
  options,
  anyValue,
  anyLabel,
  variant = "pills",
  info,
}: Props<T>) => {
  const isAny = value === anyValue;

  if (variant === "segmented") {
    return (
      <FilterWrapper icon={icon} title={title} info={info}>
        <div
          role="radiogroup"
          aria-label={title}
          className="grid auto-cols-fr grid-flow-col rounded-xl border border-input bg-muted/60 p-1"
        >
          {[{ label: anyLabel, value: anyValue }, ...options].map((option) => {
            const selected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setValue(option.value)}
                className={twMerge(
                  "flex min-h-9 min-w-0 items-center justify-center rounded-lg px-1 text-sm font-medium tabular-nums text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                  selected &&
                    "bg-background font-semibold text-foreground shadow-sm ring-1 ring-primary",
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </FilterWrapper>
    );
  }

  return (
    <FilterWrapper
      icon={icon}
      title={title}
      info={info}
      summary={isAny ? anyLabel : undefined}
      clear={() => setValue(anyValue)}
      showClear={!isAny}
    >
      <div role="group" aria-label={title} className="flex flex-wrap gap-2">
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              onClick={() => setValue(selected ? anyValue : option.value)}
              className={twMerge(
                "flex min-h-10 items-center gap-1.5 rounded-full border border-input bg-background px-3.5 text-sm font-medium transition-colors hover:border-zinc-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1",
                selected &&
                  "border-primary bg-secondary font-semibold text-secondary-foreground hover:border-primary",
              )}
            >
              {selected && (
                <Check className="-ml-0.5 size-3.5" strokeWidth={2.5} aria-hidden />
              )}
              {option.label}
            </button>
          );
        })}
      </div>
    </FilterWrapper>
  );
};

export default FilterChoice;
